import React, { useCallback, useState } from "react";
import Layout from "@components/layout";
import { Body, Link, PageHeader, SectionHeader } from "@sprout/typography";
import useTranslation from "@hooks/use_translation";
import { TermsOfServiceForm } from "@components/terms_of_service/form";
import Container from "@components/ui/container.tsx";
import { Trans } from "react-i18next";
import { router } from "@inertiajs/react";
import { PrimaryButton } from "@sprout/buttons";

type Props = {
  email: string;
  createPath: string;
};

const NewUser = ({ email, createPath }: Props) => {
  const { t } = useTranslation("misc");

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(() => {
    setIsSubmitting(true);
    router.post(
      createPath,
      {
        email,
        accept_terms: acceptedTerms ? "1" : "0",
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        onError: (e) => {
          console.error(e);
          setError(t("defaultError"));
        },
        onFinish: () => {
          setIsSubmitting(false);
        },
      },
    );
  }, [isSubmitting, acceptedTerms, email, createPath]);

  return (
    <Container>
      <div className="flex flex-col gap-[12px]">
        <PageHeader>{t("newUser.header")}</PageHeader>
        <SectionHeader>{t("newUser.subheader", { email: email })}</SectionHeader>
      </div>
      <Body>
        <Trans
          i18nKey="misc:newUser:job_alerts"
          components={{
            1: (
              <Link external href={`mailto:${email}`}>
                {email}
              </Link>
            ),
          }}
          values={{ email }}
        />
      </Body>

      <TermsOfServiceForm error={error} onChange={setAcceptedTerms} value={acceptedTerms} />
      <div className="submit-buttons-stack">
        <PrimaryButton onClick={submit} loading={isSubmitting} disabled={!acceptedTerms}>
          {t("submit")}
        </PrimaryButton>
      </div>
    </Container>
  );
};

NewUser.layout = (page: React.ReactNode) => <Layout publicFacing>{page}</Layout>;

export default NewUser;
